import { pinReplenishmentInstrument, pinReplenishmentRequirement, refreshReplenishmentRun, type Factory, type IdleMaterial, type IdleSku, type InstrumentRow, type InstrumentType, type RequirementRow } from '@api/replenishment-engine';
import DataSheet, { type DataSheetColumn } from '@components/ui/DataSheet';
import useToaster from '@hooks/toaster/useToaster';
import { ActionIcon, Badge, Box, Button, Group, HoverCard, LoadingOverlay, NumberInput, Select, Stack, Text, Tooltip } from '@mantine/core';
import { ArrowCounterClockwise, ArrowLeft, PushPin, PushPinSlash } from '@phosphor-icons/react';
import { formatCount } from '@utils/currency';
import { useCallback, useEffect, useMemo, useRef, useState, type FC, type ReactElement, type ReactNode } from 'react';

import { ApproveGate } from './ApproveGate';
import { addDaysKey, dayLabel, localDateKey, whenLabel } from './dates';
import { partsByInstrument, sizeFromLanding, tiersLabel, unlockLabel, waitsOnLabel } from './dips';
import classes from './engine.module.css';
import { IdleMaterialSheet, IdleSkuSheet } from './IdlePanel';
import { LAND_LABEL, STATUS_COLOR } from './status';
import { Lines, StatusBadge } from './tables';

const n = (v: number, maxDecimals = 0): string => formatCount(v, { maxDecimals });

export type PivotKind = 'PRODUCTION' | 'MATERIAL' | 'FG' | 'TRANSFER';

const PARTY_WORD: Record<PivotKind, [string, string]> = { PRODUCTION: ['factory', 'factories'], MATERIAL: ['vendor', 'vendors'], FG: ['vendor', 'vendors'], TRANSFER: ['lane', 'lanes'] };

/**
 * A row of a party's table: an instrument, or a dip the window cannot start (`blocked` set),
 * shown for the record at the top of its SKU's runs and never approvable. The stub carries
 * just enough of an instrument for the shared columns to place it.
 */
type PartyRow = InstrumentRow & { blocked?: RequirementRow };

const blockedStub = (req: RequirementRow, type: InstrumentType): PartyRow => ({
    id: `blocked:${req.id}`,
    type,
    status: 'PROPOSED',
    productId: req.productId,
    sku: req.sku,
    requirementSeq: req.seq,
    itemKind: 'PRODUCT',
    itemId: req.madeAsProductId ?? req.productId,
    itemName: req.productName,
    unit: null,
    fromFacilityId: req.facilityId,
    toFacilityId: null,
    vendorId: null,
    vendorName: null,
    vendorCandidates: [],
    choicePending: false,
    leadDays: null,
    qty: 0,
    tranche: req.part,
    groupKey: '',
    placeBy: req.startDate ?? req.placeBy ?? '',
    needBy: req.landDate ?? '',
    landDate: req.landDate ?? '',
    dependsOnInstrumentIds: [],
    dependsOn: [],
    feeds: [],
    requirement: { dipDate: req.dipDate, zeroDate: req.zeroDate, stockoutDays: req.stockoutDays, landStatus: req.landStatus, startDate: req.startDate, landDate: req.landDate },
    pinned: null,
    claimedBy: null,
    reason: {},
    approvedAt: null,
    approvedQty: null,
    documentType: null,
    documentId: null,
    documentCode: null,
    expectedLandDate: null,
    receivedQty: 0,
    blocked: req,
});

/** A blocked dip's first part, as words: when it can start, or why it never will. */
const firstPartText = (req: RequirementRow): string => (req.part === 0 ? (req.landStatus === 'COVERED' ? 'covered' : req.landStatus === 'UNSOURCEABLE' ? 'unsourceable' : 'nothing to start') : `${n(req.qty)} starts ${dayLabel(req.startDate)}`);

/** Instruments interleaved with blocked dips: each SKU's blocked rows first, then its runs, SKUs by their earliest date. */
const interleave = (rows: InstrumentRow[], blocked: RequirementRow[], type: InstrumentType): PartyRow[] => {
    if (blocked.length === 0) return rows;
    const bySku = new Map<string, { at: string; blocked: PartyRow[]; runs: InstrumentRow[] }>();
    const slot = (productId: string, at: string): { at: string; blocked: PartyRow[]; runs: InstrumentRow[] } => {
        const cur = bySku.get(productId) ?? { at, blocked: [], runs: [] };
        cur.at = cur.at < at ? cur.at : at;
        bySku.set(productId, cur);
        return cur;
    };
    for (const r of rows) slot(r.productId, r.placeBy).runs.push(r);
    for (const b of blocked) slot(b.productId, b.startDate ?? b.placeBy ?? '9999').blocked.push(blockedStub(b, type));
    return [...bySku.values()].sort((a, b) => a.at.localeCompare(b.at)).flatMap((s) => [...s.blocked, ...s.runs]);
};

const partyOf = (r: InstrumentRow): { key: string; label: string } =>
    r.type === 'TRANSFER'
        ? { key: `${r.fromFacilityId}>${r.toFacilityId}`, label: `${r.fromFacilityId} → ${r.toFacilityId}` }
        : r.choicePending && !r.vendorId
          ? { key: 'choose', label: 'Choose a vendor' }
          : { key: r.vendorId ?? r.fromFacilityId ?? 'none', label: r.vendorName ?? r.vendorId ?? r.fromFacilityId ?? 'No vendor mapped' };

type Party = { key: string; label: string; rows: InstrumentRow[]; qty: number; earliest: string; late: number; open: InstrumentRow[]; blocked: RequirementRow[] };

const partiesOf = (rows: InstrumentRow[], today: string): Party[] => {
    const m = new Map<string, { label: string; rows: InstrumentRow[] }>();
    for (const r of rows) {
        const p = partyOf(r);
        m.set(p.key, { label: p.label, rows: [...(m.get(p.key)?.rows ?? []), r] });
    }
    return [...m.entries()]
        .map(([key, v]) => ({ key, ...v, qty: v.rows.reduce((s, r) => s + r.qty, 0), earliest: v.rows.map((r) => r.placeBy).sort()[0], late: v.rows.filter((r) => r.placeBy <= today && r.status === 'PROPOSED').length, open: v.rows.filter((r) => r.status === 'PROPOSED'), blocked: [] as RequirementRow[] }))
        .sort((a, b) => a.earliest.localeCompare(b.earliest) || a.label.localeCompare(b.label));
};

const whyOf = (r: InstrumentRow, reqOf: Map<string, RequirementRow>): InstrumentRow['requirement'] => {
    if (r.requirement) return r.requirement;
    const req = reqOf.get(`${r.productId}|${r.requirementSeq}`);
    return req ? { dipDate: req.dipDate, zeroDate: req.zeroDate, stockoutDays: req.stockoutDays, landStatus: req.landStatus, startDate: req.startDate, landDate: req.landDate } : null;
};

/** The dip a line answers, as one cell: when the FG dips and how bad it gets. */
const DipCell: FC<{ r: InstrumentRow; reqOf: Map<string, RequirementRow>; today: string; onOpenSku: (productId: string) => void }> = ({ r, reqOf, today, onOpenSku }) => {
    const why = whyOf(r, reqOf);
    const l = why ? LAND_LABEL[why.landStatus] : null;
    return (
        <div
            className={classes.cellLink}
            role="button"
            tabIndex={0}
            onClick={(e) => {
                e.stopPropagation();
                onOpenSku(r.productId);
            }}
        >
            <Lines lines={[{ text: why ? `${r.sku} dips ${whenLabel(why.dipDate, today)}` : r.sku, late: Boolean(why?.dipDate && why.dipDate <= today), sub: why ? `zero ${dayLabel(why.zeroDate)}${why.stockoutDays > 0 ? ` · ${why.stockoutDays} days at zero` : l ? ` · ${l.text}` : ''}` : '', tone: why && why.stockoutDays > 0 ? 'red' : l?.tone }]} />
        </div>
    );
};

/** What a run needs before it starts: its orders and transfers, counted by status, with the detail on hover. */
const NeedsCell: FC<{ r: InstrumentRow; today: string }> = ({ r, today }) => {
    const deps = r.dependsOn;
    if (deps.length === 0) return <Lines lines={[{ text: 'all material on the floor', tone: 'green' }]} />;
    const pos = deps.filter((d) => d.type === 'MATERIAL_PO');
    const moves = deps.filter((d) => d.type === 'TRANSFER');
    const open = deps.filter((d) => d.status === 'PROPOSED').length;
    const gate = [...deps].sort((a, b) => b.landDate.localeCompare(a.landDate))[0];
    const lateGate = open > 0 && gate.landDate > r.placeBy;
    return (
        <HoverCard width={360} shadow="md" openDelay={150} withinPortal>
            <HoverCard.Target>
                <Group gap={6} wrap="nowrap">
                    {pos.length > 0 && (
                        <Badge size="sm" variant={pos.every((d) => d.status !== 'PROPOSED') ? 'filled' : 'light'} color="orange" tt="none">
                            {pos.length} PO{pos.length === 1 ? '' : 's'}
                        </Badge>
                    )}
                    {moves.length > 0 && (
                        <Badge size="sm" variant={moves.every((d) => d.status !== 'PROPOSED') ? 'filled' : 'light'} color="violet" tt="none">
                            {moves.length} transfer{moves.length === 1 ? '' : 's'}
                        </Badge>
                    )}
                    <Text size="10px" c={open === 0 ? 'teal' : lateGate ? 'red' : 'dimmed'}>
                        {open === 0 ? 'all approved' : `${open} to place`}
                    </Text>
                </Group>
            </HoverCard.Target>
            <HoverCard.Dropdown>
                <Stack gap={4}>
                    <Text size="xs" fw={600}>
                        Before {r.itemName ?? r.sku}{r.tranche > 1 ? ` part ${r.tranche}` : ''} starts {whenLabel(r.placeBy, today)} · only what this part needs
                    </Text>
                    {deps.map((d) => (
                        <Group key={d.id} gap={6} wrap="nowrap" justify="space-between">
                            <Text size="xs" truncate>
                                {d.type === 'TRANSFER' ? 'Move' : 'Order'} {n(d.qty, 1)} {d.itemName ?? d.type}
                            </Text>
                            <Group gap={4} wrap="nowrap">
                                <Text size="10px" c={d.landDate > r.placeBy ? 'red' : 'dimmed'}>
                                    lands {dayLabel(d.landDate)}
                                </Text>
                                <StatusBadge status={d.status} />
                                {d.documentCode && (
                                    <Badge size="xs" variant="outline" tt="none">
                                        {d.documentCode}
                                    </Badge>
                                )}
                            </Group>
                        </Group>
                    ))}
                </Stack>
            </HoverCard.Dropdown>
        </HoverCard>
    );
};

/** What a line feeds: the runs it goes into, as one pill, every run and the dip behind each on hover. */
const FeedsCell: FC<{ r: InstrumentRow; reqOf: Map<string, RequirementRow>; today: string }> = ({ r, reqOf, today }) => {
    const runs = r.feeds;
    const first = runs[0];
    const last = runs[runs.length - 1];
    const why = whyOf(r, reqOf);
    const allDone = runs.length > 0 && runs.every((x) => x.status !== 'PROPOSED');
    const coverTo = (r.reason.coverToDate as string | null | undefined) ?? null;
    const label = !first ? `for ${r.sku}` : runs.length === 1 ? `${first.type === 'FG_PO' ? 'order' : 'run'} ${first.sku} · ${dayLabel(first.placeBy)}` : `${runs.length} runs · ${dayLabel(first.placeBy)} → ${dayLabel(last.placeBy)}`;
    return (
        <HoverCard width={380} shadow="md" openDelay={150} withinPortal>
            <HoverCard.Target>
                <Badge size="sm" variant={allDone ? 'filled' : 'light'} color={first ? (allDone ? STATUS_COLOR[first.status] : 'blue') : 'gray'} tt="none" className={classes.cellLink}>
                    {label}
                </Badge>
            </HoverCard.Target>
            <HoverCard.Dropdown>
                <Stack gap={4}>
                    <Text size="xs" fw={600}>
                        This lands {dayLabel(r.landDate)}, needed by {dayLabel(r.needBy)}
                        {coverTo && runs.length > 1 ? ` · one order for every run starting up to ${dayLabel(coverTo)}` : ''}
                    </Text>
                    {runs.map((run) => (
                            <Group key={run.id} gap={6} wrap="nowrap" justify="space-between">
                                <Text size="xs" truncate>
                                    {run.type === 'FG_PO' ? 'Order' : 'Run'} {run.sku}
                                    {run.tranche > 1 ? ` part ${run.tranche}` : ''} · {n(run.qty)} · starts {dayLabel(run.placeBy)}
                                    {run.inputQty != null ? ` · draws ${n(run.inputQty, r.unit ? 1 : 0)}${r.unit ? ` ${r.unit}` : ''}` : ''}
                                </Text>
                                <Group gap={4} wrap="nowrap">
                                    <StatusBadge status={run.status} />
                                    {run.documentCode && (
                                        <Badge size="xs" variant="outline" tt="none">
                                            {run.documentCode}
                                        </Badge>
                                    )}
                                </Group>
                            </Group>
                    ))}
                    {runs.length === 0 && why && (
                        <Text size="xs">
                            {r.sku} dips {whenLabel(why.dipDate, today)} · zero {dayLabel(why.zeroDate)}
                        </Text>
                    )}
                    {why && (
                        <Text size="10px" c="dimmed">
                            {why.stockoutDays > 0 ? `${why.stockoutDays} days at zero before it lands` : LAND_LABEL[why.landStatus].text}
                        </Text>
                    )}
                </Stack>
            </HoverCard.Dropdown>
        </HoverCard>
    );
};

/** Saved for this very window with this number: Save has nothing to write for it. */
const isSavedAsIs = (r: InstrumentRow, windowStart: string | null, windowEnd: string | null): boolean => {
    const s = r.savedCopy;
    return s != null && s.placeBy === r.placeBy && Math.abs(s.qty - r.qty) < 0.001 && s.savedPeriod?.start === windowStart && s.savedPeriod?.end === windowEnd;
};

/** The header Save: what is left to save, or that it is all saved. */
const SaveButton: FC<{ open: InstrumentRow[]; windowStart: string | null; windowEnd: string | null; onSave: (ids: string[]) => void; all?: boolean }> = ({ open, windowStart, windowEnd, onSave, all }) => {
    const left = open.filter((r) => !isSavedAsIs(r, windowStart, windowEnd)).length;
    const label = left === 0 ? 'All saved' : left === open.length ? (all ? `Save all ${open.length}` : 'Save') : `Save ${left} of ${open.length}`;
    return (
        <Button size="xs" variant="subtle" color="grape" title={left === 0 ? 'Every line is saved for this window with these numbers' : 'Opens the save sheet: what is new, what replaces an earlier save, and a tick to leave lines out'} onClick={() => onSave(open.map((r) => r.id))} disabled={left === 0}>
            {label}
        </Button>
    );
};

/** "You saved this": the latest saved copy of the same action, its day and number, and what got done against it. */
const SavedCopyNote: FC<{ r: InstrumentRow }> = ({ r }) => {
    const s = r.savedCopy!;
    const sameDay = s.placeBy === r.placeBy;
    const sameQty = Math.abs(s.qty - r.qty) < 0.001;
    const done = s.doneQty != null ? ` · ${n(s.doneQty)} done` : '';
    const forPeriod = s.savedPeriod ? (s.savedPeriod.start === s.savedPeriod.end ? dayLabel(s.savedPeriod.start) : `${dayLabel(s.savedPeriod.start)} → ${dayLabel(s.savedPeriod.end)}`) : dayLabel(s.placeBy);
    return (
        <Text size="10px" c={sameDay && sameQty ? 'grape' : 'orange'} title={`Saved ${s.savedAt ? dayLabel(s.savedAt.slice(0, 10)) : ''} for ${forPeriod}: ${n(s.qty)}. Done counts what was produced or received in that period. The plan's numbers are computed from actuals only.`}>
            saved {sameDay ? '' : `${dayLabel(s.placeBy)} `}
            {n(s.qty)}
            {sameQty ? '' : ` · now ${n(r.qty)}`}
            {done}
        </Text>
    );
};

/**
 * Where a line stands. Approve and Save act on a whole factory or vendor from its header (and
 * one line can be left out in the review sheet), so the row only says what is true of it.
 */
const LineStatus: FC<{ r: InstrumentRow }> = ({ r }) =>
    r.status === 'PROPOSED' && r.choicePending ? (
        <Text size="xs" c="dimmed">
            pick a {r.type === 'WORK_ORDER' ? 'factory' : 'vendor'} first
        </Text>
    ) : r.status === 'PROPOSED' ? (
        r.savedCopy ? (
            <SavedCopyNote r={r} />
        ) : (
            <Text size="xs" c="dimmed">
                proposed
            </Text>
        )
    ) : (
        <Group gap={4} wrap="nowrap">
            <StatusBadge status={r.status} />
            {r.documentCode && (
                <Badge size="xs" variant="outline" tt="none">
                    {r.documentCode}
                </Badge>
            )}
        </Group>
    );

/**
 * The line's vendor, as a pick when the brand has one to make: every candidate with its lead.
 * The pick is saved on the instrument and moves its dates; the page reloads.
 */
const daysBetween = (from: string, to: string): number => Math.round((new Date(`${to}T00:00:00`).getTime() - new Date(`${from}T00:00:00`).getTime()) / 86400000);

const VendorCell: FC<{ r: InstrumentRow; req?: RequirementRow; today: string; window?: { start: string | null; end: string | null }; onChanged?: () => void; capacityPerDay?: number | null }> = ({ r, req, today, window, onChanged, capacityPerDay = null }) => {
    const [busy, setBusy] = useState(false);
    const [draft, setDraft] = useState<string | null>(null);
    const word = r.type === 'WORK_ORDER' ? 'factory' : 'vendor';
    // The day the line has to land (a run: its dip; a material order: the run it feeds starts;
    // an FG order: its need-by). A candidate whose lead no longer fits is late by the difference.
    const target = r.type === 'WORK_ORDER' ? (r.requirement?.dipDate ?? r.landDate) : r.type === 'MATERIAL_PO' ? (r.feeds[0]?.placeBy ?? r.needBy) : r.needBy;
    const room = daysBetween(today, target);
    const lateBy = (lead: number): number => Math.max(0, lead - room);
    // What the line becomes with this pick, the way the server will date it: the landing it
    // needs less the lead to there, never before today; the landing follows.
    // The quantity read again at the new landing, the way the server does on save: only for an
    // order or a run with its dip's series on hand.
    const qtyAt = (lands: string): number | null => {
        const series = req?.series;
        if (!series || !req || (r.type !== 'FG_PO' && r.type !== 'WORK_ORDER')) return null;
        const q = sizeFromLanding({ stock: series.stock, demand: series.demand, inbound: series.inbound, onHand: req.onHand, landDay: daysBetween(series.from, lands), refillDays: req.refillDays });
        return q > 0 ? q : null;
    };
    const preview = (vendorId: string): { placeBy: string; lands: string; late: number; outside: boolean; qty: number | null } | null => {
        const c = r.vendorCandidates.find((x) => x.vendorId === vendorId);
        if (!c) return null;
        const lead = r.type === 'WORK_ORDER' ? c.leadDays : Math.max(0, c.leadDays - (c.viaLeadDays ?? 0));
        const need = r.type === 'WORK_ORDER' ? (r.requirement?.dipDate ?? r.landDate) : r.needBy;
        const wanted = addDaysKey(need, -lead);
        const placeBy = wanted < today ? today : wanted;
        const lands = addDaysKey(placeBy, lead);
        const outside = Boolean(window && ((window.start && placeBy < window.start) || (window.end && placeBy > window.end)));
        return { placeBy, lands, late: Math.max(0, daysBetween(need, lands)), outside, qty: qtyAt(lands) };
    };
    const save = useCallback(async () => {
        if (!draft) return;
        setBusy(true);
        try {
            await pinReplenishmentInstrument(r.id, { vendorId: draft });
            setDraft(null);
            onChanged?.();
        } finally {
            setBusy(false);
        }
    }, [r.id, draft, onChanged]);
    if (r.status === 'PROPOSED' && r.vendorCandidates.length > 1) {
        const p = draft && draft !== r.vendorId ? preview(draft) : null;
        return (
            <div onClick={(e) => e.stopPropagation()} role="presentation">
                <Stack gap={4}>
                    <Select
                        size="xs"
                        value={draft ?? (r.vendorId || null)}
                        data={r.vendorCandidates.map((c) => ({ value: c.vendorId, label: `${c.vendorName ?? c.vendorId} · ${c.leadDays}d${lateBy(c.leadDays) > 0 ? ` · late by ${lateBy(c.leadDays)}d` : ''}` }))}
                        onChange={(v) => setDraft(v)}
                        disabled={busy}
                        placeholder={r.choicePending ? `choose a ${word}` : `change ${word}`}
                    />
                    {p && (
                        <>
                            <Lines lines={[{ text: `place by ${whenLabel(p.placeBy, today)} · lands ${dayLabel(p.lands)}`, late: p.late > 0, sub: `${p.late > 0 ? `lands ${p.late}d after it is needed` : 'lands in time'}${p.qty != null && p.qty !== r.qty ? ` · qty ${n(r.qty)} → ${n(p.qty)}` : ''}${p.outside ? ' · moves outside this window' : ''}` }]} />
                            <Group gap={4} wrap="nowrap">
                                <Button size="compact-xs" onClick={() => void save()} loading={busy}>
                                    Save
                                </Button>
                                <Button size="compact-xs" variant="subtle" onClick={() => setDraft(null)} disabled={busy}>
                                    Cancel
                                </Button>
                            </Group>
                        </>
                    )}
                </Stack>
            </div>
        );
    }
    const name = r.vendorName ?? r.vendorId ?? `no ${word} mapped`;
    return <Lines lines={[{ text: capacityPerDay != null ? `${name} · ${n(capacityPerDay)} / day` : name, late: !r.vendorName && !r.vendorId, sub: r.leadDays != null ? `${r.leadDays}d lead` : '' }]} />;
};

/**
 * The brand's edits before they are planned: per line, a quantity to fix or a clear. Nothing
 * is sent until Re-plan; then each edit is pinned on its line and the engine runs once
 * around all of them.
 */
export type PinEdit = { qty: number } | { clear: true };
/** A line's number before and after the last Re-plan, when it moved. */
export type QtyChange = { from: number; to: number };
export interface PinEdits {
    byId: Map<string, PinEdit>;
    onEdit: (instrumentId: string, edit: PinEdit | null) => void;
    /** Lines whose number the last Re-plan moved, by identity key — the violet flash. */
    changed?: Map<string, QtyChange>;
}

/** The same line across runs: proposals get new ids every run, so edits and diffs key on what the line is. */
const lineKey = (r: Pick<InstrumentRow, 'type' | 'productId' | 'requirementSeq' | 'tranche' | 'itemId' | 'fromFacilityId' | 'toFacilityId'>): string =>
    `${r.type}|${r.productId}|${r.requirementSeq}|${r.tranche}|${r.itemId}|${r.fromFacilityId ?? ''}|${r.toFacilityId ?? ''}`;

/**
 * The number that goes on the order or run: the engine's number until the brand types
 * another. A typed number is an edit the moment it is typed (violet box) until Re-plan,
 * then a pin the engine plans around (a run on its day, an order on its date) that
 * approve carries. The pin icon in the box clears it; the next plan returns the line to
 * the engine's own number. After a Re-plan, a box whose number moved flashes violet with
 * the old number on hover. Nothing is written under the box.
 */
const QtyEditCell: FC<{ r: InstrumentRow; sub?: string; decimals?: number; edits?: PinEdits; blank?: boolean }> = ({ r, sub, decimals = 0, edits, blank = false }) => {
    // `blank`: a dip no run could start has no number of its own; the box waits for one.
    const typed = edits?.byId.get(r.id);
    const [value, setValue] = useState<number | string>(blank ? (typed && 'qty' in typed ? typed.qty : '') : r.qty);
    // A new plan brings a new number: the box follows it.
    useEffect(() => {
        if (!blank) setValue(r.qty);
    }, [r.qty, r.id, blank]);
    const edit = edits?.byId.get(r.id);
    const editQty = edit && 'qty' in edit ? edit.qty : null;
    const clearing = Boolean(edit && 'clear' in edit);
    const pinnedNow = r.pinned?.qty != null && !clearing;
    const over = Number(r.reason.overCapacity ?? 0);
    const changed = edits?.changed?.get(lineKey(r));
    const commit = useCallback(
        (next: number | string) => {
            const qty = typeof next === 'number' ? next : Number(next);
            if (!edits) return;
            if (blank && (next === '' || qty === 0)) {
                if (editQty != null) edits.onEdit(r.id, null);
                return;
            }
            if (next === '' || !Number.isFinite(qty) || qty < 0) return;
            if (qty === r.qty) {
                if (editQty != null) edits.onEdit(r.id, null);
                return;
            }
            edits.onEdit(r.id, { qty });
        },
        [r.id, r.qty, editQty, edits, blank],
    );
    if (r.status !== 'PROPOSED') return <Lines lines={[{ text: n(Number(r.approvedQty ?? r.qty), decimals), strong: true, sub: sub ?? r.status.toLowerCase() }]} />;
    if (!edits) return <Lines lines={[{ text: n(r.qty, decimals), strong: true, sub }]} />;
    const inputClass = editQty != null ? classes.qtyEdited : clearing ? classes.qtyClearing : over > 0 ? classes.qtyOver : changed ? classes.qtyChanged : pinnedNow ? classes.qtyPinned : undefined;
    const hint = editQty != null
        ? blank
            ? 'Made anyway: Re-plan starts this run today, whatever the material or the factory has free.'
            : `Edited — was ${n(r.qty, decimals)}. Re-plan to fix it.`
        : blank
          ? 'No run fits the window. Type a quantity to make it anyway.'
        : clearing
          ? "Pin cleared — the engine's number comes back on Re-plan."
          : over > 0
            ? `Fixed by hand, ${n(over)} above what the factory had free that day`
            : changed
              ? `Re-planned: was ${n(changed.from, decimals)}`
              : pinnedNow
                ? 'Fixed by hand — the engine plans around it every run'
                : sub;
    const icon = editQty != null ? (
        <Tooltip label="Undo the edit" withArrow>
            <ActionIcon size="xs" variant="subtle" color="violet" aria-label="Undo the edit" onClick={() => { edits.onEdit(r.id, null); setValue(blank ? '' : r.qty); }}>
                <ArrowCounterClockwise size={12} />
            </ActionIcon>
        </Tooltip>
    ) : clearing ? (
        <Tooltip label="Keep the pin" withArrow>
            <ActionIcon size="xs" variant="subtle" color="gray" aria-label="Keep the pin" onClick={() => edits.onEdit(r.id, null)}>
                <PushPinSlash size={12} />
            </ActionIcon>
        </Tooltip>
    ) : pinnedNow ? (
        <Tooltip label="Fixed by hand — click to unpin" withArrow>
            <ActionIcon size="xs" variant="subtle" color={over > 0 ? 'red' : 'violet'} aria-label="Unpin" onClick={() => edits.onEdit(r.id, { clear: true })}>
                <PushPin size={12} weight="fill" />
            </ActionIcon>
        </Tooltip>
    ) : null;
    return (
        <div onClick={(e) => e.stopPropagation()} role="presentation">
            <Tooltip label={hint} withArrow disabled={!hint} openDelay={300}>
                <NumberInput
                    size="xs"
                    value={value}
                    placeholder={blank ? 'make anyway' : undefined}
                    min={0}
                    step={1}
                    decimalScale={decimals}
                    thousandSeparator=","
                    classNames={inputClass ? { input: inputClass } : undefined}
                    rightSection={icon}
                    rightSectionWidth={icon ? 26 : undefined}
                    onChange={(v) => {
                        setValue(v);
                        commit(v);
                    }}
                    onBlur={() => commit(value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') commit(value);
                    }}
                />
            </Tooltip>
        </div>
    );
};

/** What the curve asked for at this run's landing, with the dip it answers. Read-only: the run's own number is edited in Final. */
const NeededCell: FC<{ req: RequirementRow; today: string }> = ({ req, today }) => {
    const l = LAND_LABEL[req.landStatus];
    const sub = `dips ${whenLabel(req.dipDate, today)} · zero ${dayLabel(req.zeroDate)}${req.stockoutDays > 0 ? ` · ${req.stockoutDays} days at zero` : l ? ` · ${l.text}` : ''}`;
    return <Lines lines={[{ text: n(req.neededAtLanding ?? req.requiredQty), strong: true, late: Boolean(req.dipDate && req.dipDate <= today), sub, tone: req.stockoutDays > 0 ? 'red' : l?.tone }]} />;
};

/**
 * One party's rows: a factory's runs, or a vendor's (or lane's) lines. The same columns
 * whichever page shows them, so a line reads the same under an objective tab and a card.
 */
const PartyTable: FC<{ kind: PivotKind; rows: InstrumentRow[]; blocked: RequirementRow[]; windowStart: string | null; windowEnd: string | null; reqOf: Map<string, RequirementRow>; partOf: Map<string, RequirementRow>; today: string; onOpenSku: (productId: string) => void; onOpenMaterial?: (materialId: string, siteId: string) => void; onChanged?: () => void; edits?: PinEdits; title?: ReactNode; actions?: ReactNode; emailName?: string; factories?: Record<string, Factory> }> = ({ kind, rows, blocked, windowStart, windowEnd, reqOf, partOf, today, onOpenSku, onOpenMaterial, onChanged, edits, title, actions, emailName, factories }) => {
    const data = useMemo(() => interleave(rows, blocked, kind === 'FG' ? 'FG_PO' : 'WORK_ORDER'), [rows, blocked, kind]);
    const columns = useMemo<DataSheetColumn<PartyRow>[]>(() => {
        const blockedBadge = (): ReactElement => (
            <Tooltip label="Type a quantity in Final quantity and Re-plan to make it anyway, starting today" withArrow openDelay={300}>
                <Badge size="xs" variant="outline" color="gray" tt="none">
                    cannot start in window
                </Badge>
            </Tooltip>
        );
        const waitsOn = (b: RequirementRow): string | null => (b.part === 0 ? waitsOnLabel(b.gatingMaterialId, b.chain) : waitsOnLabel(b.unlockedBy ?? b.gatingMaterialId, b.chain));
        if (kind === 'PRODUCTION') {
            return [
                {
                    key: 'sku',
                    header: 'SKU to produce',
                    width: 220,
                    sticky: true,
                    sortable: true,
                    captureDetailHeader: 'On hand · cover',
                    valueAccessor: (r) => r.itemName ?? r.itemId,
                    render: (_v, r): ReactElement => {
                        if (r.blocked) return <Lines lines={[{ text: r.itemName ?? r.itemId, strong: true }, { text: '', sub: `${r.itemId !== r.productId ? `for ${r.sku} · ` : ''}dips ${whenLabel(r.blocked.dipDate, today)} · zero ${dayLabel(r.blocked.zeroDate)}`, tone: 'orange' }]} />;
                        const req = reqOf.get(`${r.productId}|${r.requirementSeq}`);
                        return <Lines lines={[{ text: r.itemName ?? r.itemId, strong: true }, { text: '', sub: `${r.itemId !== r.productId ? `for ${r.sku} · ` : ''}${req ? `on hand ${n(req.onHand)}${req.coverDays != null ? ` · ${n(req.coverDays)}d cover` : ''}` : ''}` }]} />;
                    },
                },
                {
                    key: 'needed',
                    header: 'Needed',
                    width: 170,
                    align: 'right',
                    sortable: true,
                    valueAccessor: (r) => reqOf.get(`${r.productId}|${r.requirementSeq}`)?.requiredQty ?? 0,
                    render: (_v, r): ReactElement => {
                        // The whole replenishment: what refills the SKU to its cover from the day it dips.
                        const req = r.blocked ?? reqOf.get(`${r.productId}|${r.requirementSeq}`);
                        if (!req) return <Lines lines={[]} />;
                        if (r.blocked) {
                            const l = LAND_LABEL[req.landStatus];
                            return <Lines lines={[{ text: n(req.requiredQty), strong: true, late: Boolean(req.dipDate && req.dipDate <= today), sub: `dips ${whenLabel(req.dipDate, today)} · zero ${dayLabel(req.zeroDate)}${req.stockoutDays > 0 ? ` · ${req.stockoutDays} days at zero` : l ? ` · ${l.text}` : ''}`, tone: 'orange' }]} />;
                        }
                        return <NeededCell req={partOf.get(r.id) ?? req} today={today} />;
                    },
                },
                {
                    key: 'qty',
                    header: 'Suggested',
                    width: 210,
                    align: 'right',
                    sortable: true,
                    valueAccessor: (r) => r.qty,
                    render: (_v, r): ReactElement => {
                        if (r.blocked) return <Lines lines={[{ text: `nothing can start by ${dayLabel(windowEnd)}`, strong: true, tone: 'orange', sub: r.blocked.part === 0 ? firstPartText(r.blocked) : `needed ${n(r.blocked.requiredQty)}` }]} />;
                        // What the factory can take of it on this start day, by material and capacity.
                        // One dip is one run: what the curve still lacks after it lands is the next
                        // dip, its own row, so the sub says only what this one is of and when that is.
                        const suggested = Number(r.reason.suggestedQty ?? r.qty);
                        const part = partOf.get(r.id);
                        const req = reqOf.get(`${r.productId}|${r.requirementSeq}`);
                        const needed = part?.neededAtLanding ?? req?.requiredQty ?? null;
                        const nextDip = reqOf.get(`${r.productId}|${r.requirementSeq + 1}`);
                        const gating = (r.reason.gatingMaterialId as string | null | undefined) ?? null;
                        const bits: string[] = [];
                        if (needed != null && suggested < needed) {
                            bits.push(`of ${n(needed)} needed by ${dayLabel(part?.landDate ?? r.landDate)}${gating ? ` · ${waitsOnLabel(gating, part?.chain)} short` : ''}`);
                            if (nextDip?.dipDate) bits.push(`next dip ${dayLabel(nextDip.dipDate)}`);
                        } else if (needed != null) bits.push('all of it');
                        const serves = tiersLabel(part?.tiers ?? (r.reason.tiers as RequirementRow['tiers'] | undefined));
                        if (serves) bits.push(serves);
                        const short = needed != null && suggested < needed;
                        return <Lines lines={[{ text: n(suggested), strong: true, sub: bits.join(' · '), tone: short ? 'orange' : undefined }]} />;
                    },
                },
                { key: 'final', header: 'Final quantity', width: 150, align: 'right', sortable: true, valueAccessor: (r) => r.qty, render: (_v, r): ReactElement => <QtyEditCell r={r} edits={edits} blank={Boolean(r.blocked)} /> },
                { key: 'start', header: 'Start', width: 130, sortable: true, valueAccessor: (r) => r.placeBy, render: (_v, r): ReactElement => (r.blocked ? <Lines lines={[{ text: firstPartText(r.blocked), tone: r.blocked.part === 0 ? (r.blocked.landStatus === 'COVERED' ? 'green' : 'red') : 'orange' }]} /> : <Lines lines={[{ text: whenLabel(r.placeBy, today), late: r.placeBy <= today && r.status === 'PROPOSED', strong: true, sub: `lands ${dayLabel(r.landDate)}` }]} />) },
                { key: 'needs', header: 'Needs first', width: 190, render: (_v, r): ReactElement => (r.blocked ? <Lines lines={[{ text: waitsOn(r.blocked) ? `waits on ${waitsOn(r.blocked)}` : r.blocked.part === 0 ? firstPartText(r.blocked) : `unlocked by ${unlockLabel(r.blocked.unlockedBy, r.blocked.chain)}`, tone: 'orange' }]} /> : <NeedsCell r={r} today={today} />) },
                { key: 'factory', header: 'Factory', width: 220, render: (_v, r): ReactElement => (r.blocked ? <Text size="sm" c="dimmed">—</Text> : <VendorCell r={r} req={reqOf.get(`${r.productId}|${r.requirementSeq}`)} today={today} window={{ start: windowStart, end: windowEnd }} onChanged={onChanged} capacityPerDay={factories?.[r.vendorId ?? r.fromFacilityId ?? '']?.capacityPerDay ?? null} />) },
                { key: 'approve', header: '', width: 190, render: (_v, r): ReactElement => (r.blocked ? blockedBadge() : <LineStatus r={r} />) },
            ];
        }
        const transfer = kind === 'TRANSFER';
        return [
            { key: 'item', header: transfer ? 'Material to move' : 'Item', width: 240, sticky: true, sortable: true, valueAccessor: (r) => r.itemName ?? r.itemId, render: (_v, r): ReactElement => (r.blocked ? <Lines lines={[{ text: r.itemName ?? r.itemId, strong: true }, { text: '', sub: `finished good · dips ${whenLabel(r.blocked.dipDate, today)} · zero ${dayLabel(r.blocked.zeroDate)}`, tone: 'orange' }]} /> : <Lines lines={[{ text: r.itemName ?? r.itemId, strong: true }, { text: '', sub: `${r.unit ?? ''}${r.type === 'FG_PO' ? ' · finished good' : ''}${r.toFacilityId ? ` · to ${r.toFacilityId}` : ''}` }]} />) },
            {
                key: 'qty',
                header: 'Qty',
                width: 170,
                align: 'right',
                sortable: true,
                valueAccessor: (r) => r.qty,
                render: (_v, r): ReactElement => {
                    // A bought SKU's dip no order fits can still be ordered by hand, like a run in Production.
                    if (r.blocked && kind === 'FG') return <QtyEditCell r={r} sub={`nothing can start by ${dayLabel(windowEnd)}`} edits={edits} blank />;
                    if (r.blocked) return <Lines lines={[{ text: `nothing can start by ${dayLabel(windowEnd)}`, strong: true, tone: 'orange', sub: r.blocked.part === 0 ? firstPartText(r.blocked) : `needed ${n(r.blocked.requiredQty)}` }]} />;
                    const part = partOf.get(r.id);
                    const coverTo = (r.reason.coverToDate as string | null | undefined) ?? null;
                    const sub = r.type === 'FG_PO' && part?.neededAtLanding != null ? `of ${n(part.neededAtLanding)} needed by ${dayLabel(part.landDate)}` : r.feeds.length > 1 ? `${r.feeds.length} runs${coverTo ? ` to ${dayLabel(coverTo)}` : ''}` : '';
                    if (transfer) return <Lines lines={[{ text: n(r.qty, r.unit ? 1 : 0), strong: true, sub }]} />;
                    return <QtyEditCell r={r} sub={sub} decimals={r.unit ? 1 : 0} edits={edits} />;
                },
            },
            ...(transfer ? [] : [{ key: 'vendor', header: 'Vendor', width: 220, render: (_v: unknown, r: PartyRow): ReactElement => (r.blocked ? <Text size="sm" c="dimmed">—</Text> : <VendorCell r={r} req={reqOf.get(`${r.productId}|${r.requirementSeq}`)} today={today} window={{ start: windowStart, end: windowEnd }} onChanged={onChanged} />) } as DataSheetColumn<PartyRow>]),
            { key: 'placeBy', header: transfer ? 'Dispatch by' : 'Place by', width: 150, sortable: true, valueAccessor: (r) => r.placeBy, render: (_v, r): ReactElement => (r.blocked ? <Lines lines={[{ text: firstPartText(r.blocked), tone: r.blocked.part === 0 ? (r.blocked.landStatus === 'COVERED' ? 'green' : 'red') : 'orange' }]} /> : <Lines lines={[{ text: whenLabel(r.placeBy, today), late: r.placeBy <= today && r.status === 'PROPOSED', strong: true, sub: r.leadDays != null ? `${r.leadDays}d lead` : '' }]} />) },
            { key: 'lands', header: transfer ? 'Arrives' : 'Lands by', width: 150, sortable: true, valueAccessor: (r) => r.landDate, render: (_v, r): ReactElement => (r.blocked ? <Lines lines={[{ text: waitsOn(r.blocked) ? `waits on ${waitsOn(r.blocked)}` : '—', tone: 'orange' }]} /> : <Lines lines={[{ text: dayLabel(r.landDate), sub: r.feeds[0] ? `needed ${dayLabel(r.needBy)} for the ${r.feeds.length > 1 ? 'first run' : r.feeds[0].type === 'FG_PO' ? 'order' : 'run'}` : `needed ${dayLabel(r.needBy)}`, late: r.landDate > r.needBy }]} />) },
            { key: 'feeds', header: 'Feeds', width: 220, render: (_v, r): ReactElement => (r.blocked ? <DipCell r={r} reqOf={reqOf} today={today} onOpenSku={onOpenSku} /> : <FeedsCell r={r} reqOf={reqOf} today={today} />) },
            { key: 'approve', header: '', width: 190, render: (_v, r): ReactElement => (r.blocked ? blockedBadge() : <LineStatus r={r} />) },
        ];
    }, [kind, reqOf, partOf, windowStart, windowEnd, today, onOpenSku, onChanged, edits, factories]);
    // The standard header bar: the party's name on the left, Approve/Save then AI view, Email, Download on the right, like every other grid.
    return <DataSheet<PartyRow> data={data} columns={columns} getRowKey={(r) => r.id} getRowClassName={(r) => (r.blocked ? classes.blockedRow : '')} rowHeight={52} maxHeight="auto" title={title} toolbarActionsRight={actions} emailName={emailName} enableColumnPicker columnPickerKey={`replen_pivot_${kind}_cols_v1`} onRowClick={(r) => {
        // A material line or transfer opens the material's story at the site it feeds; a run or FG order opens the SKU's.
        if ((kind === 'MATERIAL' || kind === 'TRANSFER') && !r.blocked && r.itemKind === 'MATERIAL' && onOpenMaterial && r.toFacilityId) onOpenMaterial(r.itemId, r.toFacilityId);
        else onOpenSku(r.productId);
    }} />;
};

/**
 * A pivot: production orders per factory, purchase orders per vendor, transfers per lane.
 * One section per party with its own approve-all, a dropdown to look at one party at a
 * time, and the same columns under an objective tab and under a card.
 */
const PivotPanel: FC<{ kind: PivotKind; rows: InstrumentRow[]; skus: RequirementRow[]; blocked?: RequirementRow[]; idleSkus?: IdleSku[]; idleMaterials?: IdleMaterial[]; windowStart?: string | null; windowEnd?: string | null; title: string; subtitle: string; onOpenSku: (productId: string) => void; onOpenMaterial?: (materialId: string, siteId: string) => void; onApprove: (ids: string[]) => void; onSave?: (ids: string[]) => void; onBack?: () => void; onChanged?: () => void | Promise<void>; factories?: Record<string, Factory> }> = ({ kind, rows, skus, blocked = [], idleSkus = [], idleMaterials = [], windowStart = null, windowEnd = null, title, subtitle, onOpenSku, onOpenMaterial, onApprove, onSave, onBack, onChanged, factories }) => {
    const today = localDateKey();
    const [party, setParty] = useState<string>('ALL');
    // Edits are gathered here and sent together on Re-plan: each is pinned on its line, then
    // the engine runs once and plans around all of them.
    const [edits, setEdits] = useState<Map<string, PinEdit>>(new Map());
    const [planning, setPlanning] = useState(false);
    const [planError, setPlanError] = useState<string | null>(null);
    // What every line said before Re-plan, so the new plan can be read against it: the
    // boxes that moved flash violet and the toast lists them by SKU.
    const beforeReplan = useRef<Map<string, { sku: string; itemName: string | null; qty: number }> | null>(null);
    const [changed, setChanged] = useState<Map<string, QtyChange>>(new Map());
    const { showToastSuccess } = useToaster();
    useEffect(() => {
        const before = beforeReplan.current;
        if (!before) return;
        beforeReplan.current = null;
        const moved = new Map<string, QtyChange>();
        const lines: string[] = [];
        for (const r of rows) {
            if (r.status !== 'PROPOSED') continue;
            const was = before.get(lineKey(r));
            if (!was || was.qty === r.qty) continue;
            moved.set(lineKey(r), { from: was.qty, to: r.qty });
            lines.push(`${r.sku}${r.itemKind === 'MATERIAL' ? ` · ${r.itemName ?? r.itemId}` : ''}: ${n(was.qty, r.unit ? 1 : 0)} → ${n(r.qty, r.unit ? 1 : 0)}`);
        }
        const gone = before.size - rows.filter((r) => r.status === 'PROPOSED' && before.has(lineKey(r))).length;
        const added = rows.filter((r) => r.status === 'PROPOSED' && !before.has(lineKey(r))).length;
        setChanged(moved);
        const shown = lines.slice(0, 6);
        const more = lines.length - shown.length;
        const extras = [gone > 0 ? `${gone} line${gone === 1 ? '' : 's'} dropped` : '', added > 0 ? `${added} new line${added === 1 ? '' : 's'}` : ''].filter(Boolean).join(' · ');
        showToastSuccess({
            title: moved.size === 0 && gone === 0 && added === 0 ? 'Re-planned — nothing moved' : `Re-planned — ${moved.size} number${moved.size === 1 ? '' : 's'} changed`,
            autoClose: 8000,
            subtitle: (
                <Stack gap={2}>
                    {shown.map((line) => (
                        <Text key={line} size="xs">
                            {line}
                        </Text>
                    ))}
                    {more > 0 && (
                        <Text size="xs" c="dimmed">
                            +{more} more
                        </Text>
                    )}
                    {extras && (
                        <Text size="xs" c="dimmed">
                            {extras}
                        </Text>
                    )}
                </Stack>
            ),
        });
    }, [rows, showToastSuccess]);
    const pinEdits = useMemo<PinEdits>(
        () => ({
            byId: edits,
            changed,
            onEdit: (id, edit) =>
                setEdits((e) => {
                    const next = new Map(e);
                    if (edit) next.set(id, edit);
                    else next.delete(id);
                    return next;
                }),
        }),
        [edits, changed],
    );
    const editCount = edits.size;
    const pinnedCount = rows.filter((r) => r.status === 'PROPOSED' && r.pinned?.qty != null).length;
    const replan = useCallback(async () => {
        setPlanning(true);
        setPlanError(null);
        setChanged(new Map());
        try {
            for (const [id, edit] of edits) {
                // A dip no run could start has no line yet: its number becomes a pinned run.
                const dip = id.startsWith('blocked:') ? blocked.find((b) => `blocked:${b.id}` === id) : null;
                if (dip) {
                    if ('qty' in edit && edit.qty > 0) await pinReplenishmentRequirement({ productId: dip.productId, qty: edit.qty, startDate: windowStart ?? undefined, until: windowEnd ?? undefined });
                } else await pinReplenishmentInstrument(id, 'qty' in edit ? { ...edit, until: windowEnd ?? undefined } : edit);
            }
            await refreshReplenishmentRun();
            setEdits(new Map());
            beforeReplan.current = new Map(rows.filter((r) => r.status === 'PROPOSED').map((r) => [lineKey(r), { sku: r.sku, itemName: r.itemName, qty: r.qty }]));
            await onChanged?.();
        } catch (e) {
            beforeReplan.current = null;
            setPlanError(e instanceof Error ? e.message : 'Could not re-plan');
        } finally {
            setPlanning(false);
        }
    }, [edits, rows, onChanged, blocked, windowStart, windowEnd]);
    // What the window has nothing for, and why: off by default, one click away.
    const [showIdle, setShowIdle] = useState(false);
    const materialKind = kind === 'MATERIAL' || kind === 'TRANSFER';
    const idleCount = materialKind ? idleMaterials.length : idleSkus.length;
    const idleNoun = materialKind ? `material${idleMaterials.length === 1 ? '' : 's'} with nothing to order` : `SKU${idleSkus.length === 1 ? '' : 's'} with nothing to ${kind === 'PRODUCTION' ? 'start' : 'order'}`;
    // The dip's head row per dip, and the part row per run or order: what each line's dip asked for.
    const reqOf = useMemo(() => new Map(skus.filter((s) => s.part <= 1).map((s) => [`${s.productId}|${s.seq}`, s])), [skus]);
    const partOf = useMemo(() => partsByInstrument(skus), [skus]);
    const parties = useMemo(() => {
        const found = partiesOf(rows, today);
        if (blocked.length === 0) return found;
        // A blocked dip sits with the party its SKU's runs go to, else with its own facility; a
        // SKU with no runs at all gets a party of its own so it is still on the page.
        const partyBySku = new Map<string, string>();
        for (const p of found) for (const r of p.rows) if (!partyBySku.has(r.productId)) partyBySku.set(r.productId, p.key);
        const extra = new Map<string, Party>();
        for (const b of blocked) {
            const key = partyBySku.get(b.productId) ?? b.facilityId ?? 'none';
            const target = found.find((p) => p.key === key) ?? extra.get(key) ?? { key, label: b.facilityId ?? 'No route', rows: [], qty: 0, earliest: b.startDate ?? b.placeBy ?? '', late: 0, open: [], blocked: [] };
            target.blocked = [...target.blocked, b];
            if (!found.includes(target)) extra.set(key, target);
        }
        return [...found, ...extra.values()];
    }, [rows, blocked, today]);
    const shown = party === 'ALL' ? parties : parties.filter((p) => p.key === party);
    const [one, many] = PARTY_WORD[kind];
    const openAll = rows.filter((r) => r.status === 'PROPOSED');
    // The bar's "all" buttons only when they reach further than one header's: two or more
    // parties with open lines. With one, they would be that header's buttons twice.
    const openParties = parties.filter((p) => p.open.length > 0).length;
    return (
        <Stack gap="sm">
            <Group className={classes.bar} justify="space-between" align="center">
                <Group gap="sm" align="center" wrap="nowrap">
                    {onBack && (
                        <Button size="xs" variant="default" leftSection={<ArrowLeft size={14} />} onClick={onBack}>
                            Objectives
                        </Button>
                    )}
                    <Stack gap={0}>
                        <Text fw={700}>{title}</Text>
                    <Text size="xs" c="dimmed">
                        {subtitle} · {parties.length} {parties.length === 1 ? one : many}
                    </Text>
                    </Stack>
                </Group>
                <Group gap="xs" align="center">
                    <Select size="xs" w={300} value={party} onChange={(v) => setParty(v ?? 'ALL')} data={[{ value: 'ALL', label: `All ${many} (${rows.length} line${rows.length === 1 ? '' : 's'})` }, ...parties.map((p) => ({ value: p.key, label: `${p.label} · ${p.rows.length} line${p.rows.length === 1 ? '' : 's'}${p.late ? ` · ${p.late} due` : ''}` }))]} searchable />
                    {party === 'ALL' && openParties > 1 && (
                        <ApproveGate>
                            <Button size="xs" variant="light" onClick={() => onApprove(openAll.map((r) => r.id))}>
                                Review all {openAll.length}
                            </Button>
                        </ApproveGate>
                    )}
                    {party === 'ALL' && openParties > 1 && onSave && (
                        <SaveButton open={openAll} windowStart={windowStart} windowEnd={windowEnd} onSave={onSave} all />
                    )}
                    {idleCount > 0 && (
                        <Button size="xs" variant={showIdle ? 'light' : 'default'} onClick={() => setShowIdle((v) => !v)}>
                            {showIdle ? 'Hide' : 'Show'} {idleCount} {idleNoun}
                        </Button>
                    )}
                </Group>
            </Group>
            {(editCount > 0 || pinnedCount > 0 || planError) && (
                <Group className={classes.bar} justify="space-between" align="center">
                    <Group gap="xs" align="center" wrap="nowrap">
                        {editCount > 0 ? (
                            <>
                                <Badge size="sm" color="orange" variant="light" tt="none">
                                    {editCount} edit{editCount === 1 ? '' : 's'} not planned yet
                                </Badge>
                                <Text size="xs" c="dimmed">
                                    Re-plan reads the plan again around every number you fixed; the rest follows.
                                </Text>
                            </>
                        ) : (
                            <Text size="xs" c="dimmed">
                                {pinnedCount} line{pinnedCount === 1 ? '' : 's'} fixed by hand · the engine plans around them every run; unpin a line to return it to the engine's number.
                            </Text>
                        )}
                        {planError && (
                            <Text size="xs" c="red">
                                {planError}
                            </Text>
                        )}
                    </Group>
                    <Group gap="xs" align="center" wrap="nowrap">
                        {changed.size > 0 && editCount === 0 && (
                            <Badge size="sm" color="violet" variant="light" tt="none">
                                {changed.size} number{changed.size === 1 ? '' : 's'} moved on the last re-plan
                            </Badge>
                        )}
                        {editCount > 0 && (
                            <>
                                <Button size="xs" variant="subtle" onClick={() => setEdits(new Map())} disabled={planning}>
                                    Discard
                                </Button>
                                <Button size="xs" onClick={() => void replan()} loading={planning}>
                                    Re-plan with {editCount} edit{editCount === 1 ? '' : 's'}
                                </Button>
                            </>
                        )}
                    </Group>
                </Group>
            )}
            {rows.length === 0 && blocked.length === 0 && (
                <Text size="sm" c="dimmed">
                    Nothing to place in this window.{idleCount > 0 ? ` ${idleCount} ${idleNoun}: the button above says why.` : ''}
                </Text>
            )}
            {blocked.length > 0 && (
                <Text size="xs" c="dimmed">
                    {blocked.length} dip{blocked.length === 1 ? '' : 's'} on or before {dayLabel(windowEnd)} cannot start inside the window; shown greyed with what each waits on.
                </Text>
            )}
            <Box pos="relative">
            <LoadingOverlay visible={planning} zIndex={5} overlayProps={{ radius: 'sm', blur: 1 }} loaderProps={{ type: 'dots' }} />
            {shown.map((p) => (
                <div key={p.key} className={classes.section}>
                    {(() => {
                        const head = (
                            <div className={classes.lines}>
                                <div className={classes.line} data-strong="true">
                                    {p.label}
                                    {kind === 'PRODUCTION' && factories?.[p.key]?.capacityPerDay != null ? ` · ${n(factories[p.key].capacityPerDay ?? 0)} / day` : ''}
                                </div>
                                <div className={classes.sub}>
                                    {p.rows.length} line{p.rows.length === 1 ? '' : 's'} · {n(p.qty, kind === 'PRODUCTION' ? 0 : 1)} {kind === 'PRODUCTION' ? 'units' : ''}
                                    {p.rows.length > 0 ? ` · earliest ${whenLabel(p.earliest, today)}` : ''}
                                    {p.late > 0 ? ` · ${p.late} already due` : ''}
                                    {p.blocked.length > 0 ? ` · ${p.blocked.length} blocked` : ''}
                                    {p.open.length === 0 ? ' · all approved' : p.open.length < p.rows.length ? ` · ${p.rows.length - p.open.length} approved` : ''}
                                </div>
                            </div>
                        );
                        const actions =
                            p.open.length > 0 ? (
                                <Group gap={4} wrap="nowrap">
                                    <ApproveGate>
                                        <Button size="xs" onClick={() => onApprove(p.open.map((r) => r.id))}>
                                            Review &amp; approve {p.open.length === p.rows.length ? 'all' : `${p.open.length} open`}
                                        </Button>
                                    </ApproveGate>
                                    {onSave && (
                                        <SaveButton open={p.open} windowStart={windowStart} windowEnd={windowEnd} onSave={onSave} />
                                    )}
                                </Group>
                            ) : p.rows.length > 0 ? (
                                <Badge size="sm" color="teal" variant="light" tt="none">
                                    all approved
                                </Badge>
                            ) : null;
                        return (
                            <PartyTable kind={kind} rows={p.rows} blocked={p.blocked} windowStart={windowStart} windowEnd={windowEnd} reqOf={reqOf} partOf={partOf} today={today} onOpenSku={onOpenSku} onOpenMaterial={onOpenMaterial} onChanged={onChanged} edits={pinEdits} title={head} actions={actions} emailName={`${title} — ${p.label}`} factories={factories} />
                        );
                    })()}
                </div>
            ))}
            </Box>
            {showIdle && idleCount > 0 && (
                <div className={classes.section}>
                    <div className={classes.sectionHead}>
                        <div className={classes.lines}>
                            <div className={classes.line} data-strong="true">
                                {materialKind ? 'Materials with nothing to order' : `SKUs with nothing to ${kind === 'PRODUCTION' ? 'start' : 'order'}`} in this window
                            </div>
                            <div className={classes.sub}>
                                {idleCount} {materialKind ? (idleMaterials.length === 1 ? 'material' : 'materials') : idleSkus.length === 1 ? 'SKU' : 'SKUs'} · {windowStart && windowEnd ? `${dayLabel(windowStart)} → ${dayLabel(windowEnd)}` : 'this window'} · {materialKind ? 'the materials the planned runs draw on, read off the latest run' : 'why each one needs nothing here, read off the latest run'}
                            </div>
                        </div>
                    </div>
                    {materialKind ? <IdleMaterialSheet rows={idleMaterials} today={today} onOpenMaterial={onOpenMaterial} /> : <IdleSkuSheet rows={idleSkus} kind={kind === 'PRODUCTION' ? 'PRODUCTION' : 'FG'} today={today} onOpenSku={onOpenSku} />}
                </div>
            )}
        </Stack>
    );
};

export default PivotPanel;
